import React, { useEffect, useState } from 'react';
import { getCardiologists } from '../../../../rtk-apis/users/getCardiologists';
import { AppDispatch, RootState } from '../../../../store/store';
import { useDispatch, useSelector } from 'react-redux';
import { assignReportToDoctor } from '../../../../rtk-apis/reports/assignReportToDoctor';
import { Failed } from '../../../../components';
import PersonPickerDialog from '../../../../components/PersonPickerDialog';
import { capitalizeFirstLetterOfEachWord } from '../../../../Utils/utils';
import { clearState } from '../../../../store/slices/reports/assignReportToDoctor';

type Cardiologist = {
  _id: string;
  name?: string;
  email?: string;
};

type Props = {
  onClose?: () => void;
  reportId: string;
  AssignReportStatus: string;
  setOnAssigningReport: React.Dispatch<any>;
  isAssignDoctorModalOpen: boolean;
  onAssignSuccess?: (doctorName: string) => void;
};

/**
 * Assign the cardiologist who will report a study.
 *
 * The picker itself is `PersonPickerDialog` (shared with the report's Sonographer
 * picker); everything specific to assignment stays here — the cardiologist list, the
 * assign call, and the worklist side effects it triggers on the backend.
 */
const AssignDoctorModal = ({
  onClose,
  reportId,
  setOnAssigningReport,
  AssignReportStatus,
  isAssignDoctorModalOpen,
  onAssignSuccess,
}: Props) => {
  const dispatch = useDispatch<AppDispatch>();

  const [openFailureModal, setOpenFailureModal] = useState(false);
  const [selectedDoctor, setSelectedDoctor] = useState('');
  const [failureMessage, setFailureMessage] = useState('');
  const [doctorsList, setDoctorsList] = useState<Cardiologist[]>([]);
  const [search, setSearch] = useState('');

  const { data, loading: cardiologistsLoading } = useSelector(
    (state: RootState) => state.getCardiologistsResponse
  );
  const { status, msg, loading } = useSelector(
    (state: RootState) => state.assignReportToDoctorResponse
  );

  // Fetch the clinic's cardiologists when the modal opens.
  // Assign and Re-assign read the SAME list; they used to hit two different endpoints,
  // which is why Re-assign/Update offered receptionists and admins. Both now show every
  // cardiologist in the clinic, including the report's current assignee — excluding them
  // left a single-cardiologist clinic staring at an empty dialog.
  useEffect(() => {
    if (isAssignDoctorModalOpen) {
      dispatch(getCardiologists({}));
    } else {
      dispatch(clearState());
      setDoctorsList([]); // Reset list when modal closes
      setSelectedDoctor(''); // Reset dropdown selection
      setSearch('');
    }
  }, [isAssignDoctorModalOpen]);

  // Update doctors list when API response changes
  useEffect(() => {
    setDoctorsList(Array.isArray(data) ? data : []);
  }, [data]);

  // Handle assignment response.
  //
  // Success is silent. Nikhil, 16 Sep: "when assigning doctor or sono, remove the pop up
  // which says assigned - its unnecessary" — the assignee's name appears in the row the
  // moment the list refreshes, which says the same thing without a modal and a wait.
  // Failure still shows: that one carries information the user cannot see anywhere else.
  useEffect(() => {
    if (status === 'SUCCESS') {
      setOnAssigningReport(true); // ReportList refetches and clears this itself
      const selectedDoctorObj = doctorsList.find(d => d._id === selectedDoctor);
      const doctorName = selectedDoctorObj
        ? capitalizeFirstLetterOfEachWord(selectedDoctorObj.name)
        : '';
      dispatch(clearState());
      onAssignSuccess?.(doctorName);
      onClose();
    } else if (status === 'FAILED') {
      setOpenFailureModal(true);
      setFailureMessage(msg);
      const timer = setTimeout(() => {
        setOpenFailureModal(false);
        setFailureMessage('');
      }, 1500);
      return () => clearTimeout(timer); // Cleanup timer
    }
  }, [status]);

  const handleConfirmAssign = () => {
    if (!selectedDoctor) {
      setOpenFailureModal(true);
      setFailureMessage('Please select a doctor before assigning.');
      return;
    }
    const payload = {
      userId: selectedDoctor,
      reportIds: [reportId],
    };
    dispatch(assignReportToDoctor(payload));
  };

  return (
    <>
      <PersonPickerDialog
        open={!!isAssignDoctorModalOpen}
        onClose={onClose}
        title={
          AssignReportStatus === 'Reassign' ? 'Re-assign Cardiologist' : 'Assign a Cardiologist'
        }
        subtitle="Select the cardiologist who will report this study."
        people={doctorsList}
        loading={cardiologistsLoading}
        emptyText="No cardiologists found for this clinic."
        noMatchText={term => `No cardiologist matches "${term}".`}
        search={search}
        onSearchChange={setSearch}
        selectedId={selectedDoctor}
        onSelect={setSelectedDoctor}
        onConfirm={handleConfirmAssign}
        busy={loading}
        confirmLabel="Yes, Confirm"
        busyLabel="Assigning…"
      />

      {openFailureModal && (
        <Failed
          isFailedModalOpen={openFailureModal}
          handleCloseModal={() => setOpenFailureModal(false)}
          title={failureMessage}
        />
      )}
    </>
  );
};

export default AssignDoctorModal;
